import { useTranslation, useEvent } from "@godgpt/react";
import { useState } from "react";

interface EventLogEntry {
  id: string;
  time: string;
  type: string;
  data: string;
}

export function I18nPlayground() {
  const { t, locale, setLocale, supportedLocales } = useTranslation();
  const [events, setEvents] = useState<EventLogEntry[]>([]);

  useEvent("i18n:localeChanged", (...args: unknown[]) => {
    const data = args[0] as { locale: string; previousLocale: string };
    addEvent("i18n:localeChanged", `${data?.previousLocale} → ${data?.locale}`);
  });

  function addEvent(type: string, data: string) {
    const now = new Date();
    const time = now.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setEvents((prev) => [
      { id: `${Date.now()}-${Math.random()}`, time, type, data },
      ...prev.slice(0, 19),
    ]);
  }

  const localeNames: Record<string, string> = {
    en: "English",
    es: "Español",
    fr: "Français",
    de: "Deutsch",
  };

  const testKeys = [
    "nav.home",
    "nav.auth",
    "nav.api",
    "nav.i18n",
    "common.loading",
    "common.signIn",
    "common.signOut",
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">🌍 i18n Playground</h1>
        <p className="page-subtitle">
          Test internationalization and locale switching
        </p>
      </div>

      <div className="grid grid-2">
        {/* Locale Selector */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-icon">🗣️</span>
              Current Locale
            </div>
            <span className="card-badge success">{locale}</span>
          </div>

          <div className="form-group">
            <label className="label">Select Language</label>
            <select
              className="input select"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {supportedLocales.map((loc) => (
                <option key={loc} value={loc}>
                  {localeNames[loc] || loc} ({loc})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-sm" style={{ flexWrap: "wrap" }}>
            {supportedLocales.map((loc) => (
              <button
                key={loc}
                className={`btn ${loc === locale ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setLocale(loc)}
              >
                {localeNames[loc] || loc}
              </button>
            ))}
          </div>
        </div>

        {/* Translation Test */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-icon">📝</span>
              Translation Output
            </div>
          </div>

          <div className="event-log" style={{ maxHeight: "250px" }}>
            {testKeys.map((key) => (
              <div key={key} className="event-item">
                <span className="event-type" style={{ minWidth: "140px" }}>
                  {key}
                </span>
                <span className="event-data">{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Log */}
      <div className="card mt-lg">
        <div className="card-header">
          <div className="card-title">
            <span className="card-icon">📋</span>
            Locale Change Events
          </div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setEvents([])}
            disabled={events.length === 0}
          >
            Clear
          </button>
        </div>
        <div className="event-log">
          {events.length === 0 ? (
            <div className="event-empty">
              No events yet. Try changing the locale to see events.
            </div>
          ) : (
            events.map((event) => (
              <div key={event.id} className="event-item">
                <span className="event-time">{event.time}</span>
                <span className="event-type">{event.type}</span>
                <span className="event-data">{event.data}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Code Example */}
      <div className="card mt-lg">
        <div className="card-header">
          <div className="card-title">
            <span className="card-icon">💻</span>
            Code Example
          </div>
        </div>
        <div className="code-block">
{`import { useTranslation } from "@godgpt/react";

function LocalizedComponent() {
  const { t, locale, setLocale, supportedLocales } = useTranslation();

  return (
    <div>
      <h1>{t("app.title")}</h1>
      <p>{t("welcome.message", { name: "User" })}</p>

      <select value={locale} onChange={(e) => setLocale(e.target.value)}>
        {supportedLocales.map((loc) => (
          <option key={loc} value={loc}>{loc}</option>
        ))}
      </select>
    </div>
  );
}`}
        </div>
      </div>
    </div>
  );
}
